import { useState, useEffect, useCallback } from 'react';
import { getTasks, createTask, updateTask, completeTask, deleteTask, getLeads } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { 
  Plus, CheckCircle2, Circle, Clock, AlertCircle, Trash2, 
  Calendar, User, Link, Loader2, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isPast, isToday } from 'date-fns';

const priorityColors = {
  low: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  urgent: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const statusIcons = {
  pending: Circle,
  in_progress: Clock,
  completed: CheckCircle2,
};

export const TasksView = () => {
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, in_progress, completed
  const [showNewTask, setShowNewTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, leadsRes] = await Promise.all([
        getTasks(filter === 'all' ? null : filter),
        getLeads()
      ]);
      setTasks(tasksRes.data);
      setLeads(leadsRes.data);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleComplete = async (task) => {
    try {
      if (task.status === 'completed') {
        await updateTask(task.id, { status: 'pending' });
        toast.success('Task reopened');
      } else {
        await completeTask(task.id);
        toast.success('Task completed!');
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await deleteTask(taskId);
      toast.success('Task deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead?.business_name || 'Unknown';
  };

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    overdue: tasks.filter(t => t.due_date && isPast(parseISO(t.due_date)) && t.status !== 'completed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background p-4" data-testid="tasks-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-accent" />
            Tasks
          </h2>
          <p className="text-sm text-muted-foreground">
            {stats.total} total • {stats.pending} pending • {stats.overdue > 0 && <span className="text-red-400">{stats.overdue} overdue</span>}
          </p>
        </div>
        <Button onClick={() => setShowNewTask(true)} className="bg-accent hover:bg-accent/90">
          <Plus className="w-4 h-4 mr-2" />
          New Task
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        {['all', 'pending', 'in_progress', 'completed'].map(f => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? 'bg-accent' : ''}
          >
            {f === 'all' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            {f === 'all' && <Badge variant="secondary" className="ml-2">{stats.total}</Badge>}
            {f === 'pending' && <Badge variant="secondary" className="ml-2">{stats.pending}</Badge>}
            {f === 'in_progress' && <Badge variant="secondary" className="ml-2">{stats.in_progress}</Badge>}
            {f === 'completed' && <Badge variant="secondary" className="ml-2">{stats.completed}</Badge>}
          </Button>
        ))}
      </div>

      {/* Task List */}
      <div className="flex-1 overflow-auto">
        {tasks.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg">No tasks found</p>
            <p className="text-sm">Create a new task to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.map(task => {
              const StatusIcon = statusIcons[task.status] || Circle;
              const isOverdue = task.due_date && isPast(parseISO(task.due_date)) && task.status !== 'completed';
              const isDueToday = task.due_date && isToday(parseISO(task.due_date));
              
              return (
                <div
                  key={task.id}
                  className={`p-4 bg-card rounded-lg border transition-all hover:border-accent/50 ${
                    task.status === 'completed' ? 'opacity-60' : ''
                  } ${isOverdue ? 'border-red-500/50' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => handleToggleComplete(task)}
                      className={`mt-0.5 ${task.status === 'completed' ? 'text-green-400' : 'text-muted-foreground hover:text-accent'}`}
                    >
                      <StatusIcon className="w-5 h-5" />
                    </button>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-medium ${task.status === 'completed' ? 'line-through' : ''}`}>
                          {task.title}
                        </span>
                        <Badge variant="outline" className={priorityColors[task.priority] || priorityColors.medium}>
                          {task.priority}
                        </Badge>
                        {isOverdue && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Overdue
                          </Badge>
                        )}
                        {isDueToday && !isOverdue && (
                          <Badge variant="secondary" className="text-xs bg-yellow-500/20 text-yellow-400">
                            Due Today
                          </Badge>
                        )}
                      </div>
                      
                      {task.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{task.description}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {task.due_date && (
                          <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-400' : ''}`}>
                            <Calendar className="w-3 h-3" />
                            {format(parseISO(task.due_date), 'MMM d, yyyy')}
                          </span>
                        )}
                        {task.lead_id && (
                          <span className="flex items-center gap-1">
                            <Link className="w-3 h-3" />
                            {getLeadName(task.lead_id)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingTask(task)}
                        className="h-8 w-8"
                      >
                        <span className="text-xs">Edit</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(task.id)}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* New/Edit Task Modal */}
      <TaskModal
        open={showNewTask || !!editingTask}
        onClose={() => { setShowNewTask(false); setEditingTask(null); }}
        task={editingTask}
        leads={leads}
        onSaved={() => { fetchData(); setShowNewTask(false); setEditingTask(null); }}
      />
    </div>
  );
};

const TaskModal = ({ open, onClose, task, leads, onSaved }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    lead_id: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        due_date: task.due_date ? format(parseISO(task.due_date), 'yyyy-MM-dd') : '',
        lead_id: task.lead_id || '',
      });
    } else {
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        due_date: '',
        lead_id: '',
      });
    }
  }, [task]);

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        lead_id: formData.lead_id || null,
      };

      if (task) {
        await updateTask(task.id, payload);
        toast.success('Task updated');
      } else {
        await createTask(payload);
        toast.success('Task created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{task ? 'Edit Task' : 'New Task'}</DialogTitle>
          <DialogDescription>
            {task ? 'Update task details' : 'Create a new task'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Task Title *</Label>
            <Input
              placeholder="e.g., Follow up with client"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              data-testid="task-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Task details..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Link to Lead (optional)</Label>
            <Select value={formData.lead_id || "none"} onValueChange={(v) => setFormData({ ...formData, lead_id: v === "none" ? "" : v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select a lead..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-- No lead --</SelectItem>
                {leads.map(lead => (
                  <SelectItem key={lead.id} value={lead.id}>{lead.business_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-accent hover:bg-accent/90">
              {saving ? 'Saving...' : (task ? 'Update' : 'Create Task')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TasksView;
